import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useUI } from "providers/UIProvider"

import { ICustomersProviderProps, ICustomersInitialContext } from "./CustomersProviderTypes"
import { CUSTOMERS_INITIAL_CONTEXT, CUSTOMERS_INITIAL_STATE } from "./CustomersProviderConstants"
import useGetCustomers from "api/querries/customers/useGetCustomers"
import { ICustomer, IGetCustomersQueryParams } from "types/customer"
import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import { formatPhoneNumber } from "utils/formatPhoneNumber"
import useBlockUnblockCustomerMutation from "api/mutations/customers/useBlockUnblockCustomer"
import useDeleteCustomerMutation from "api/mutations/customers/useDeleteCustomer"
import useGetCustomerVehicles from "api/querries/customers/useGetCustomerVehicles"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { IRoles } from "types/shared"

export const CustomersContext = createContext<ICustomersInitialContext>(CUSTOMERS_INITIAL_CONTEXT)

export const useCustomersContext: () => ICustomersInitialContext = () => useContext(CustomersContext)

const CustomersProvider = ({ children }: ICustomersProviderProps) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { modals, toggleModal } = useUI()
  const { user } = useAppContext()

  // @ts-ignore
  const hasAccess = user && user.roles && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(user?.roles[0]) || false;

  const searchParams = new URLSearchParams(search)

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    {
      ...CUSTOMERS_INITIAL_STATE,
      searchValue: searchParams.get("search") || ""
    }
  )

  const [query, setQuery] = useState({
    search: searchParams.get("search") || "",
    sortBy: searchParams.get("sortBy") || "id",
    pageIndex: Number(searchParams.get("pageIndex")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 10,
  })

  const { data: customersData, isLoading: customersDataLoading, isFetched: customersDataFetched, refetch: refetchCustomersData } = useGetCustomers(
    { ...query },
    hasAccess,
  )
  const { data: customerVehiclesData, isFetched: customersVehiclesDataFetched, refetch: refetchCustomerVehicles } = useGetCustomerVehicles(state.currentCustomerId, Boolean(state.currentCustomerId && modals.clientAutosModal.isOpened))
  const { mutate: blockUnblockCustomer, isSuccess: blockUnblockCustomerSucces } = useBlockUnblockCustomerMutation()
  const { mutate: deleteCustomer, isSuccess: deleteCustomerSuccess } = useDeleteCustomerMutation()


  const updateSearchParams = (query: IGetCustomersQueryParams) => {
    navigate({
      search: `?${new URLSearchParams({
        ...query,
        pageIndex: query.pageIndex.toString(),
        pageSize: query.pageSize.toString()
      }).toString()}`
    })
  }

  const handleCustomersPaginationClick = (newPage: number) => {
    const updatedQuery = {
      ...query,
      pageIndex: newPage
    }
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)
  }

  const handleSearchChange = (e: any) => {
    setState({ searchValue: e.target.value })
    setTimeout(() => {
      const updatedQuery = {
        ...query,
        search: e.target.value
      }
      updateSearchParams(updatedQuery)
      setQuery(updatedQuery)
    }, 1000)
  }

  const handleSearchClick = () => {
    const updatedQuery = {
      ...query,
      pageIndex: 1,
      search: state.searchValue
    }
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)
  }


  const handleReservationsClick = (customer: ICustomer) => {
    setState({ currentCustomer: customer, currentCustomerId: customer.id })
  }

  const handleTransactionsClick = (customer: ICustomer) => {
    setState({ currentCustomer: customer, currentCustomerId: customer.id })
  }


  const handleAutosClick = (customer: ICustomer) => {
    setState({ currentCustomer: customer, currentCustomerId: customer.id })
  }

  const handleBlockUnblockClick = (customer: ICustomer) => {
    const action = customer.blocked ? "გააქტიურება" : "დაბლოკვა"
    toggleModal("confirmationModal", true, {
      title: `დარწმუნებული ხართ, რომ გსურთ მომხმარებლის, ${formatPhoneNumber(customer.userName)}, ${action}`,
      handleDeny: () => toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data),
      handleSubmit: () => blockUnblockCustomer(customer.id)
    })
  }

  const handleDeleteClick = (customer: ICustomer) => {
    toggleModal("confirmationModal", true, {
      title: `დარწმუნებული ხართ, რომ გსურთ მომხმარებლის, ${formatPhoneNumber(customer.userName)}, წაშლა`,
      handleDeny: () => toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data),
      handleSubmit: () => deleteCustomer(customer.id)
    })
  }

  useEffect(() => {
    if (customersData && customersDataFetched) {
      setState({ customers: customersData.data })
    }
  }, [customersData])

  useEffect(() => {
    if (customerVehiclesData && customersVehiclesDataFetched) {
      setState({ vehicles: customerVehiclesData.data })
    }
  }, [customerVehiclesData])

  useEffect(() => {
    if (blockUnblockCustomerSucces || deleteCustomerSuccess) {
      refetchCustomersData()
      toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
    }
  }, [blockUnblockCustomerSucces, deleteCustomerSuccess])

  useEffect(() => {
    if (state.currentCustomerId) {
      if (modals.clientAutosModal.isOpened) {
        refetchCustomerVehicles()
      }
    }
  }, [state.currentCustomerId])



  const value = useMemo(() => ({
    ...state,
    query,
    handleCustomersPaginationClick,
    handleSearchChange,
    handleSearchClick,
    handleBlockUnblockClick,
    handleDeleteClick,
    handleAutosClick,
    handleReservationsClick,
    handleTransactionsClick,
    setState,
    customersDataLoading
  }), [state, query, customersDataLoading])

  return <CustomersContext.Provider value={value}>
    {children}
  </CustomersContext.Provider>
}

export default CustomersProvider